import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Popover, Radio } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import _ from 'lodash';
import Calender, { DateRangeType } from 'components/molecules/Calender';
import dayjs from 'dayjs';
import { default as CalenderIcon } from 'assets/svg/Calender';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PSR_PROTFOLIO_INACTIVATION_DATE_CHANGED } from 'utils/mixpanelEventNames';
import { FilterContextData } from 'contexts/FilterContext';

type InactivationModalProps = {
    visible: boolean;
    closeModal: Function;
    onSaveClick: (val: any, selectedDate: string) => void;
    targetData: any;
};

const currentDate = new Date();

const InactivationModal = ({ visible, closeModal, onSaveClick, targetData }: InactivationModalProps) => {
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { calenderStartDay } = useContext(FilterContextData);
    const onCancelClick = () => {
        closeModal();
    };

    const [radioButtonSelectedOption, setRadioButtonSelectedOption] = useState<string>('isImmediate');
    const [tempSelectedDate, setTempSelectedDate] = useState<any>([null, null]);
    const [selectedDate, setSelectedDate] = useState<any>([null, null]);

    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        setSelectedDate([null, null]);
        setTempSelectedDate([null, null]);
        setRadioButtonSelectedOption('isImmediate');
        if ('inActivationDate' in targetData && targetData?.inActivationDate[0]) {
            setRadioButtonSelectedOption(targetData.inActivationDate[0] ? 'inactivationDate' : 'isImmediate');
            setSelectedDate([null, new Date(targetData.inActivationDate[0] * 1000)]);
            setTempSelectedDate([null, new Date(targetData.inActivationDate[0] * 1000)]);
        }
    }, [targetData, visible]);

    const dateLabel = useMemo(() => {
        let startLabel = 'Start Date';
        if (selectedDate[1]) startLabel = dayjs(new Date(selectedDate[1])).format('MM/DD/YYYY');
        return { startLabel };
    }, [selectedDate]);

    const handleOpenChange = (newOpen: boolean, isClickOutside: boolean = true) => {
        setIsOpen(newOpen);
        if (!newOpen && !isClickOutside) {
            //only save the date when clicked on set date button
            setSelectedDate(tempSelectedDate);
            trackEventMixpanel(PSR_PROTFOLIO_INACTIVATION_DATE_CHANGED, {
                date: tempSelectedDate[1],
            });
        } else if (!newOpen) {
            //if clicked outside dont save the change,
            setTempSelectedDate(selectedDate);
        }
    };

    const handleDateChange = (range: DateRangeType) => {
        setTempSelectedDate([...range]);
    };

    const onSaveClickHandler = () => {
        onSaveClick(radioButtonSelectedOption === 'isImmediate', selectedDate);
        setSelectedDate([null, null]);
        setTempSelectedDate([null, null]);
        setRadioButtonSelectedOption('isImmediate');
    };

    return (
        <StyledModal
            width={300}
            open={visible}
            title=""
            onCancel={onCancelClick}
            footer={null}
            className="rounded "
            centered
        >
            <div className="flex flex-col">
                <div className="rounded overflow-hidden text-subHeaderColor font-lg mb-3 font-medium">Set End Date</div>
                <Radio.Group defaultValue="isImmediate" style={{ display: 'flex', flexDirection: 'column' }}>
                    <Radio
                        value="isImmediate"
                        checked={radioButtonSelectedOption === 'isImmediate'}
                        children={
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span> Immediate</span>
                                <span style={{ maxWidth: '200px', fontSize: '11px' }}>
                                    First date of the simulation period will be selected as InActive Date
                                </span>
                            </div>
                        }
                        onChange={() => setRadioButtonSelectedOption('isImmediate')}
                    />
                    <Radio
                        value="inactivationDate"
                        checked={radioButtonSelectedOption === 'inactivationDate'}
                        children={
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span> Select Date</span>
                                <Popover
                                    trigger="click"
                                    open={isOpen}
                                    onOpenChange={handleOpenChange}
                                    placement={'right'}
                                    content={
                                        <Calender
                                            minYear={currentDate.getFullYear()}
                                            startYear={currentDate.getFullYear()}
                                            maxYear={currentDate.getFullYear() + 2}
                                            disabledDates={[dayjs(currentDate).format('MM-DD-YYYY'), null]}
                                            startDay={calenderStartDay}
                                            range={tempSelectedDate}
                                            onChange={handleDateChange}
                                            onFooterClick={() => handleOpenChange(false, false)}
                                            singleDateSelect={true}
                                        />
                                    }
                                >
                                    <div className="flex justify-center items-center w-36">
                                        <div
                                            className={`  cursor-pointer w-full flex justify-start gap-1 py-1 items-center px-1 border border-purpleFont rounded`}
                                        >
                                            <div className="flex w-full">
                                                <span
                                                    className={`${
                                                        !isOpen
                                                            ? 'text-subHeaderColor'
                                                            : dateLabel.startLabel !== 'Start Date'
                                                            ? 'text-greyFont'
                                                            : 'text-borderGrey'
                                                    }  font-xs`}
                                                >
                                                    {dateLabel.startLabel}
                                                </span>
                                            </div>
                                            <CalenderIcon />
                                        </div>
                                    </div>
                                </Popover>
                            </div>
                        }
                        onChange={() => setRadioButtonSelectedOption('inactivationDate')}
                    />
                </Radio.Group>
                <div className="flex justify-end font-bold mt-4">
                    <button
                        type="button"
                        className="mr-2 px-6 py-2 rounded cursor-pointer text-purpleFont bg-white border border-purpleFont font-xxs"
                        onClick={onCancelClick}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={`px-6 py-2 rounded border font-xxs ${
                            radioButtonSelectedOption === 'inactivationDate' && selectedDate[1] === null
                                ? 'cursor-not-allowed bg-borderGrey border-borderGrey text-promoColour'
                                : 'cursor-pointer bg-purpleFont border-purpleFont text-white'
                        } `}
                        onClick={onSaveClickHandler}
                        disabled={radioButtonSelectedOption === 'inactivationDate' && selectedDate[1] === null}
                    >
                        Save
                    </button>
                </div>
            </div>
        </StyledModal>
    );
};

export default InactivationModal;

const StyledModal = styled(Modal)`
    .ant-modal-title {
        ${tw`text-subHeaderColor`}
    }

    .ant-modal-close-x span svg {
        fill: '#140B1D';
    }

    .ant-modal-header {
        border: 0px;
    }
`;
